/**
 * @lodex-pro/web-portal/routes/RequestHaul — a pumper, foreman or admin turns a load in to a
 * hauler (OPP PR 12a).
 *
 * The targets are (tank × hauler) pairs: the tanks the caller looks after (PR 8), each with the
 * haulers holding a record of it and their job types. The form picks a tank, then one of its
 * haulers (an EXPRESS hauler working for another company shows, disabled, with the reason), then
 * one of that hauler's job types; a time wanted and notes are optional. The hauler gets a
 * PENDING job in its queue; the confirmation names the haul ID.
 *
 * One clientRequestId per submission: a double-click or a retry of the same form sends the same
 * id, so the API returns the first request instead of making a second load. Changing the form
 * starts a new submission.
 *
 * Under OP_HAUL_REQUESTS + OP_ROUTES_PUMPERS; without them the page says so in plain words. The
 * API checks SUBMIT_HAUL_REQUEST and the features; this is the UX, not the gate.
 */

import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import type {
  HaulRequestTargetView,
  PortalHaulRequestView,
} from '@lodex-pro/shared/validation/operator-portal-routes';
import { ApiError } from '@lodex-pro/web-shared';
import { usePortalMe } from '../hooks/useOperatorPortal';
import { useCreateHaulRequest, useHaulRequestTargets } from '../hooks/useHaulRequests';
import { PortalDataError } from '../components/PortalDataError';
import { portalErrorText } from '../components/portal-error-text';
import { portalHaulRequestsOnPlan, portalRequestsHauls } from '../nav/portal-features';
import { CARD, INPUT, MUTED, PRIMARY_BUTTON, BUTTON, placeLabel } from './operator-tank-labels';

/** What a caller reads when the plan has no haul requests. */
export const HAUL_REQUESTS_OFF_MESSAGE =
  "Haul requests aren't in your company's plan; ask your admin.";

/** Why an unavailable hauler is disabled, in plain words. */
export const EXPRESS_OTHER_CUSTOMER_TEXT = 'This hauler only works for one other company';

const NOTES_MAX = 1000;

const ERROR_TEXT: React.CSSProperties = { color: 'var(--color-danger)', fontSize: '13px' };
const LABEL: React.CSSProperties = { display: 'block', fontSize: '14px', marginBottom: '4px' };
const FIELD: React.CSSProperties = { marginBottom: '14px' };

function isFeatureOff(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'FEATURE_NOT_ENABLED';
}

export function HaulRequestsOff({ title }: { readonly title: string }): React.JSX.Element {
  return (
    <main style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '12px' }}>{title}</h1>
      <p role="status">{HAUL_REQUESTS_OFF_MESSAGE}</p>
    </main>
  );
}

export function RequestHaul(): React.JSX.Element {
  const me = usePortalMe();
  if (me.data === undefined) {
    return (
      <main style={{ padding: '24px' }}>
        {me.isError ? (
          <p role="alert">Could not load haul requests.</p>
        ) : (
          <p role="status">Loading…</p>
        )}
      </main>
    );
  }
  if (!portalRequestsHauls(me.data)) return <Navigate to="/portal" replace />;
  if (!portalHaulRequestsOnPlan(me.data)) return <HaulRequestsOff title="Request a haul" />;
  return <RequestHaulBody />;
}

// ── Grouping ─────────────────────────────────────────────────────────────────

interface TankChoice {
  readonly operatorTankId: string;
  readonly label: string;
  /** This tank's haulers, available ones first, each by name. */
  readonly haulers: HaulRequestTargetView[];
}

/** One entry per tank ("Oil 1 — Smith 1H"), sorted by place then tank name. */
export function groupTargetsByTank(targets: readonly HaulRequestTargetView[]): TankChoice[] {
  const tanks = new Map<
    string,
    { place: string; name: string; haulers: HaulRequestTargetView[] }
  >();
  for (const t of targets) {
    const g = tanks.get(t.operatorTankId);
    if (g === undefined) {
      tanks.set(t.operatorTankId, { place: placeLabel(t), name: t.tankName, haulers: [t] });
    } else {
      g.haulers.push(t);
    }
  }
  return [...tanks.entries()]
    .sort(([, a], [, b]) => a.place.localeCompare(b.place) || a.name.localeCompare(b.name))
    .map(([operatorTankId, g]) => ({
      operatorTankId,
      label: `${g.name} — ${g.place}`,
      haulers: [...g.haulers].sort(
        (a, b) =>
          Number(b.available) - Number(a.available) || a.haulerName.localeCompare(b.haulerName),
      ),
    }));
}

function unavailableText(t: HaulRequestTargetView): string {
  return t.unavailableReason === 'EXPRESS_OTHER_CUSTOMER'
    ? EXPRESS_OTHER_CUSTOMER_TEXT
    : 'Not taking requests right now';
}

/** The API's own words for a refused request (its error.message), else the catalog's. */
function requestErrorText(err: unknown): string {
  if (err instanceof ApiError && err.message !== '') return err.message;
  return portalErrorText(err, 'Could not send the request.');
}

// ── The form ─────────────────────────────────────────────────────────────────

function RequestHaulBody(): React.JSX.Element {
  const targets = useHaulRequestTargets();
  const create = useCreateHaulRequest();
  const [sent, setSent] = React.useState<PortalHaulRequestView | null>(null);

  if (isFeatureOff(targets.error)) return <HaulRequestsOff title="Request a haul" />;

  return (
    <main style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '4px' }}>Request a haul</h1>
      <p style={{ ...MUTED, fontSize: '13px', marginTop: 0, marginBottom: '16px' }}>
        Send a load to one of the haulers that hauls from your tanks. It goes straight to their
        dispatch.
      </p>

      {targets.isLoading && <p role="status">Loading your tanks…</p>}
      {targets.isError && (
        <PortalDataError error={targets.error} fallback="Could not load your tanks." />
      )}
      {sent !== null ? (
        <Confirmation
          request={sent}
          onAnother={(): void => {
            create.reset();
            setSent(null);
          }}
        />
      ) : (
        targets.data !== undefined &&
        (targets.data.targets.length === 0 ? (
          <NoTanks />
        ) : (
          <RequestForm targets={targets.data.targets} create={create} onSent={setSent} />
        ))
      )}
    </main>
  );
}

function NoTanks(): React.JSX.Element {
  return (
    <section aria-label="No tanks yet" style={CARD}>
      <p style={{ margin: '0 0 8px', fontSize: '14px' }}>
        You can request hauls for the tanks you look after. Pick your tanks first.
      </p>
      <Link to="/portal/my-tanks" style={{ color: 'var(--color-link)', fontSize: '14px' }}>
        Go to My tanks
      </Link>
    </section>
  );
}

function Confirmation({
  request,
  onAnother,
}: {
  readonly request: PortalHaulRequestView;
  readonly onAnother: () => void;
}): React.JSX.Element {
  return (
    <section
      aria-label="Request sent"
      style={{ ...CARD, border: '1px solid rgba(111, 207, 142, 0.4)', maxWidth: '640px' }}
    >
      <p role="status" style={{ margin: '0 0 8px', fontSize: '15px', fontWeight: 600 }}>
        Request sent to {request.haulerName}.
      </p>
      {request.haulId !== null && (
        <p style={{ margin: '0 0 12px', fontSize: '14px' }}>
          Haul ID: <strong>{request.haulId}</strong>
        </p>
      )}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <Link to="/portal/my-requests" style={{ color: 'var(--color-link)', fontSize: '14px' }}>
          See My requests
        </Link>
        <button type="button" style={BUTTON} onClick={onAnother}>
          Request another haul
        </button>
      </div>
    </section>
  );
}

function RequestForm({
  targets,
  create,
  onSent,
}: {
  readonly targets: readonly HaulRequestTargetView[];
  readonly create: ReturnType<typeof useCreateHaulRequest>;
  readonly onSent: (request: PortalHaulRequestView) => void;
}): React.JSX.Element {
  const tanks = React.useMemo(() => groupTargetsByTank(targets), [targets]);
  const [tankId, setTankId] = React.useState('');
  const [haulerId, setHaulerId] = React.useState('');
  const [jobTypeId, setJobTypeId] = React.useState('');
  const [when, setWhen] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  // One id per submission: kept across a retry of the same form, dropped when the form changes.
  const clientRequestId = React.useRef<string | null>(null);

  const tank = tanks.find((t) => t.operatorTankId === tankId) ?? null;
  const target = tank?.haulers.find((h) => h.haulerTenantId === haulerId && h.available) ?? null;
  const jobTypes = target?.jobTypes ?? [];

  const changed = (): void => {
    clientRequestId.current = null;
    setError(null);
  };

  const chooseHauler = (h: HaulRequestTargetView | undefined): void => {
    changed();
    setHaulerId(h?.haulerTenantId ?? '');
    setJobTypeId(h?.jobTypes.length === 1 ? String(h.jobTypes[0]?.id) : '');
  };

  const chooseTank = (id: string): void => {
    changed();
    setTankId(id);
    // One hauler that can take it: choose it. Otherwise the pumper chooses.
    const available = tanks
      .find((t) => t.operatorTankId === id)
      ?.haulers.filter((h) => h.available);
    chooseHauler(available?.length === 1 ? available[0] : undefined);
  };

  const canSend = target !== null && jobTypeId !== '' && !create.isPending;

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (target === null || jobTypeId === '' || create.isPending) return;
    setError(null);
    clientRequestId.current ??= crypto.randomUUID();
    const trimmed = notes.trim();
    try {
      const res = await create.mutateAsync({
        operatorTankId: target.operatorTankId,
        haulerTenantId: target.haulerTenantId,
        jobTypeId: Number(jobTypeId),
        ...(when === '' ? {} : { scheduledAt: new Date(when).toISOString() }),
        ...(trimmed === '' ? {} : { notes: trimmed }),
        clientRequestId: clientRequestId.current,
      });
      clientRequestId.current = null;
      onSent(res.request);
    } catch (err) {
      setError(requestErrorText(err));
    }
  };

  return (
    <form
      aria-label="Request a haul"
      style={{ ...CARD, maxWidth: '640px' }}
      onSubmit={(e): void => {
        void submit(e);
      }}
    >
      <div style={FIELD}>
        <label htmlFor="request-tank" style={LABEL}>
          Tank
        </label>
        <select
          id="request-tank"
          value={tankId}
          onChange={(e): void => {
            chooseTank(e.target.value);
          }}
          style={{ ...INPUT, width: '100%' }}
        >
          <option value="">Choose a tank…</option>
          {tanks.map((t) => (
            <option key={t.operatorTankId} value={t.operatorTankId}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      {tank !== null && (
        <fieldset style={{ ...FIELD, border: 'none', padding: 0, margin: '0 0 14px' }}>
          <legend style={LABEL}>Hauler</legend>
          {tank.haulers.map((h) => (
            <label
              key={h.haulerTenantId}
              style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                padding: '4px 0',
                fontSize: '14px',
                color: h.available ? undefined : 'var(--color-text-tertiary)',
              }}
            >
              <input
                type="radio"
                name="request-hauler"
                value={h.haulerTenantId}
                checked={haulerId === h.haulerTenantId}
                disabled={!h.available}
                onChange={(): void => {
                  chooseHauler(h);
                }}
              />
              <span>{h.haulerName}</span>
              {!h.available && <span style={{ fontSize: '12px' }}>— {unavailableText(h)}</span>}
            </label>
          ))}
        </fieldset>
      )}

      {target !== null && (
        <div style={FIELD}>
          <label htmlFor="request-job-type" style={LABEL}>
            Job type
          </label>
          {jobTypes.length === 0 ? (
            <p style={{ ...MUTED, fontSize: '13px', margin: 0 }}>
              This hauler has no job types set up yet.
            </p>
          ) : (
            <select
              id="request-job-type"
              value={jobTypeId}
              onChange={(e): void => {
                changed();
                setJobTypeId(e.target.value);
              }}
              style={{ ...INPUT, width: '100%' }}
            >
              <option value="">Choose a job type…</option>
              {jobTypes.map((j) => (
                <option key={j.id} value={String(j.id)}>
                  {j.label}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      <div style={FIELD}>
        <label htmlFor="request-when" style={LABEL}>
          When you want it (optional)
        </label>
        <input
          id="request-when"
          type="datetime-local"
          value={when}
          onChange={(e): void => {
            changed();
            setWhen(e.target.value);
          }}
          style={INPUT}
        />
      </div>

      <div style={FIELD}>
        <label htmlFor="request-notes" style={LABEL}>
          Notes for the hauler (optional)
        </label>
        <textarea
          id="request-notes"
          value={notes}
          maxLength={NOTES_MAX}
          rows={3}
          onChange={(e): void => {
            changed();
            setNotes(e.target.value);
          }}
          style={{ ...INPUT, width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
        />
      </div>

      {error !== null && (
        <p role="alert" style={ERROR_TEXT}>
          {error}
        </p>
      )}
      <button
        type="submit"
        style={{ ...PRIMARY_BUTTON, opacity: canSend ? 1 : 0.6 }}
        disabled={!canSend}
      >
        {create.isPending ? 'Sending…' : 'Send request'}
      </button>
    </form>
  );
}
